import { exhaustiveGuard } from "../../utils/typing"
import {
  FilterConditionExpression,
  FilterOption,
  FilterOptions,
  FilterRecord,
  FilterType,
  Filters,
  MapFiltersToFilterRecord,
  OrNode
} from "./types"

// Convert a filter type and selected options into an array of condition expressions.
// For example, ("date", ["any"]) => [{ eq: null }, { neq: null }].
export function getFilterExpressionValue<T extends FilterType>(
  filterType: T,
  value: FilterOption[]
) {
  const values = value.reduce((acc, v) => {
    switch (v) {
      case "any":
        switch (filterType) {
          case "date":
            // When "any" date, we want to match both null and non-null values.
            acc.push(
              ...([
                { eq: null },
                { neq: null }
              ] as FilterConditionExpression<T>[])
            )
            break
          case "string":
          case "number":
          case "boolean":
            break
          default:
            // No-op for now
            exhaustiveGuard(filterType)
        }
        break
      default:
        exhaustiveGuard(v)
    }

    return acc
  }, [] as FilterConditionExpression<T>[])

  return values
}

// Convert the condition expressions into a Filter object.
// For now, we are just combining multiple expressions for a single key with "or".
// In the future, we may want to support more complex logic.
export const getFilterValue = <T extends Filters>(
  filterKey: keyof T,
  conditionExpressions: FilterConditionExpression<T[keyof T]["type"]>[]
) => {
  const nextFilters: OrNode<MapFiltersToFilterRecord<T>>[] = []

  if (conditionExpressions.length) {
    // Create a new "or" node with the updated condition expressions.
    nextFilters.push({
      or: [...conditionExpressions.map((ce) => ({ [filterKey]: ce }))]
    } as OrNode<MapFiltersToFilterRecord<T>>)
  }

  // Merge the new "or" node with the other "or" nodes.
  return nextFilters
}

type TransformedFilterOptions<T extends FilterRecord> = Record<
  keyof T,
  ({ op: "or" | "and" } | FilterConditionExpression<T[keyof T]>)[] | undefined
>

// Trasnform the selected filter options into a structure suitable for GraphQL queries.
// Each filter key maps to an array of condition expressions combined with "or".
export function transformFilterOptionsToGQLFilters<T extends FilterRecord>(
  filters: T,
  filterOptions: FilterOptions<T>
): TransformedFilterOptions<T> {
  const result = Object.entries(filters).reduce((acc, [key, filterType]) => {
    const options = filterOptions[key]
    const ces = getFilterExpressionValue(filterType, options)
    const cesWithOr = ces.reduce((exprAcc, ce, idx) => {
      if (idx > 0) {
        exprAcc!.push({ op: "or" })
      }
      exprAcc!.push(ce as FilterConditionExpression<T[keyof T]>)
      return exprAcc
    }, [] as TransformedFilterOptions<T>[keyof T])

    acc[key as keyof T] = cesWithOr?.length ? cesWithOr : undefined

    return acc
  }, {} as TransformedFilterOptions<T>)

  return result
}
